import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  type CreateAdditionalTaxRequest,
  type UpdateAdditionalTaxRequest,
} from '@/models/domain/additional-tax'
import { type CreateCompanyRequest, type UpdateCompanyRequest } from '@/models/domain/company'
import {
  type CreateDBARequest,
  type CreateDistrictRequest,
  type CreateLocationRequest,
  type CreateLocationTypeRequest,
  type CreatePriceLevelRequest,
  type UpdateDBARequest,
  type UpdateDistrictRequest,
  type UpdateLocationRequest,
  type UpdateLocationTypeRequest,
  type UpdatePriceLevelRequest,
} from '@/models/domain/location'
import { locationService } from '@/services/location-service'
import { useAuthStore } from '@/stores/auth-store'

const STALE_TIME = 5 * 60 * 1000

export const LOCATIONS_QUERY_KEYS = {
  locations: (system: string | null) => ['locations', system] as const,
  location: (system: string | null, id: number) => ['locations', system, id] as const,
  locationAvailable: (system: string | null, id: number) =>
    ['locations', system, 'available', id] as const,
  dbas: (system: string | null) => ['dbas', system] as const,
  dba: (system: string | null, id: string) => ['dbas', system, id] as const,
  districts: (system: string | null) => ['districts', system] as const,
  district: (system: string | null, id: string) => ['districts', system, id] as const,
  locationTypes: (system: string | null) => ['locationTypes', system] as const,
  locationType: (system: string | null, id: string) => ['locationTypes', system, id] as const,
  priceLevels: (system: string | null) => ['priceLevels', system] as const,
  priceLevel: (system: string | null, name: string) => ['priceLevels', system, name] as const,
  companies: (system: string | null) => ['companies', system] as const,
  company: (system: string | null, id: string) => ['companies', system, id] as const,
  additionalTaxes: (system: string | null) => ['additionalTaxes', system] as const,
  additionalTax: (system: string | null, machineName: string) =>
    ['additionalTaxes', system, machineName] as const,
  systems: ['systems'] as const,
}

const LOCATIONS_INVALIDATE_KEY = ['locations']
const PRICE_LEVELS_INVALIDATE_KEY = ['priceLevels']
const COMPANIES_INVALIDATE_KEY = ['companies']
const DISTRICTS_INVALIDATE_KEY = ['districts']
const LOCATION_TYPES_INVALIDATE_KEY = ['locationTypes']
const ADDITIONAL_TAXES_INVALIDATE_KEY = ['additionalTaxes']
const DBAS_INVALIDATE_KEY = ['dbas']

// `enabled` lets callers suppress the fetch when they won't use the result
// (e.g. a dialog that only needs locations for certain modes). Defaults to true
// so existing no-arg callers are unaffected.
export function useLocations(options?: { enabled?: boolean }) {
  const { selectedSystem } = useAuthStore()

  return useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.locations(selectedSystem),
    queryFn: () => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.getLocations(selectedSystem)
    },
    enabled: (options?.enabled ?? true) && !!selectedSystem,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: 'always',
  })
}

export function useLocation(id: number | null) {
  const { selectedSystem } = useAuthStore()
  const enabled = !!selectedSystem && id !== null && Number.isFinite(id)

  return useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.location(selectedSystem, id ?? 0),
    queryFn: () => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      if (id === null) {
        throw new Error('No location id')
      }
      return locationService.getLocation(selectedSystem, id)
    },
    enabled,
    staleTime: STALE_TIME,
  })
}

export function useCreateLocation() {
  const { selectedSystem } = useAuthStore()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CreateLocationRequest) => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.createLocation(selectedSystem, request)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LOCATIONS_INVALIDATE_KEY })
    },
  })
}

export function useUpdateLocation() {
  const { selectedSystem } = useAuthStore()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpdateLocationRequest }) => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.updateLocation(selectedSystem, id, request)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LOCATIONS_INVALIDATE_KEY })
    },
  })
}

/**
 * Resolves to `true` when no Location with this id exists for the selected system.
 * Disabled until `enabled` is true so callers gate firing (e.g., on blur with a valid id).
 */
export function useLocationIdAvailable(id: number | null, enabled: boolean) {
  const { selectedSystem } = useAuthStore()

  return useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.locationAvailable(selectedSystem, id ?? 0),
    queryFn: () => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      if (id === null) {
        throw new Error('No location id')
      }
      return locationService.isLocationIdAvailable(selectedSystem, id)
    },
    enabled: enabled && !!selectedSystem && id !== null && Number.isFinite(id),
    staleTime: 0,
  })
}

export function useDBAs() {
  const { selectedSystem } = useAuthStore()

  return useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.dbas(selectedSystem),
    queryFn: () => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.getDBAs(selectedSystem)
    },
    enabled: !!selectedSystem,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: 'always',
  })
}

export function useDistricts() {
  const { selectedSystem } = useAuthStore()

  return useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.districts(selectedSystem),
    queryFn: () => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.getDistricts(selectedSystem)
    },
    enabled: !!selectedSystem,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: 'always',
  })
}

export function useLocationTypes() {
  const { selectedSystem } = useAuthStore()

  return useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.locationTypes(selectedSystem),
    queryFn: () => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.getLocationTypes(selectedSystem)
    },
    enabled: !!selectedSystem,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: 'always',
  })
}

export function useSystems() {
  return useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.systems,
    queryFn: () => locationService.getSystems(),
    staleTime: STALE_TIME,
  })
}

export function usePriceLevels() {
  const { selectedSystem } = useAuthStore()

  return useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.priceLevels(selectedSystem),
    queryFn: () => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.getPriceLevels(selectedSystem)
    },
    enabled: !!selectedSystem,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: 'always',
  })
}

export function usePriceLevel(name: string) {
  const { selectedSystem } = useAuthStore()

  return useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.priceLevel(selectedSystem, name),
    queryFn: () => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.getPriceLevel(selectedSystem, name)
    },
    enabled: !!selectedSystem && name.length > 0,
  })
}

export function useCreatePriceLevel() {
  const queryClient = useQueryClient()
  const { selectedSystem } = useAuthStore()

  return useMutation({
    mutationFn: (request: CreatePriceLevelRequest) => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.createPriceLevel(selectedSystem, request)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PRICE_LEVELS_INVALIDATE_KEY })
    },
  })
}

export function useUpdatePriceLevel() {
  const queryClient = useQueryClient()
  const { selectedSystem } = useAuthStore()

  return useMutation({
    mutationFn: ({ name, request }: { name: string; request: UpdatePriceLevelRequest }) => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.updatePriceLevel(selectedSystem, name, request)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PRICE_LEVELS_INVALIDATE_KEY })
    },
  })
}

export function useCompanies() {
  const { selectedSystem } = useAuthStore()

  return useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.companies(selectedSystem),
    queryFn: () => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.getCompanies(selectedSystem)
    },
    enabled: !!selectedSystem,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: 'always',
  })
}

export function useCompany(id: string) {
  const { selectedSystem } = useAuthStore()

  return useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.company(selectedSystem, id),
    queryFn: () => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.getCompany(selectedSystem, id)
    },
    enabled: !!selectedSystem && id.length > 0,
  })
}

export function useCreateCompany() {
  const queryClient = useQueryClient()
  const { selectedSystem } = useAuthStore()

  return useMutation({
    mutationFn: (request: CreateCompanyRequest) => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.createCompany(selectedSystem, request)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COMPANIES_INVALIDATE_KEY })
    },
  })
}

export function useUpdateCompany() {
  const queryClient = useQueryClient()
  const { selectedSystem } = useAuthStore()

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateCompanyRequest }) => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.updateCompany(selectedSystem, id, request)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COMPANIES_INVALIDATE_KEY })
    },
  })
}

export function useDistrict(id: string) {
  const { selectedSystem } = useAuthStore()

  return useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.district(selectedSystem, id),
    queryFn: () => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.getDistrict(selectedSystem, id)
    },
    enabled: !!selectedSystem && id.length > 0,
  })
}

export function useCreateDistrict() {
  const queryClient = useQueryClient()
  const { selectedSystem } = useAuthStore()

  return useMutation({
    mutationFn: (request: CreateDistrictRequest) => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.createDistrict(selectedSystem, request)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DISTRICTS_INVALIDATE_KEY })
    },
  })
}

export function useUpdateDistrict() {
  const queryClient = useQueryClient()
  const { selectedSystem } = useAuthStore()

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateDistrictRequest }) => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.updateDistrict(selectedSystem, id, request)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DISTRICTS_INVALIDATE_KEY })
    },
  })
}

export function useLocationType(id: string) {
  const { selectedSystem } = useAuthStore()

  return useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.locationType(selectedSystem, id),
    queryFn: () => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.getLocationType(selectedSystem, id)
    },
    enabled: !!selectedSystem && id.length > 0,
  })
}

export function useCreateLocationType() {
  const queryClient = useQueryClient()
  const { selectedSystem } = useAuthStore()

  return useMutation({
    mutationFn: (request: CreateLocationTypeRequest) => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.createLocationType(selectedSystem, request)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LOCATION_TYPES_INVALIDATE_KEY })
    },
  })
}

export function useUpdateLocationType() {
  const queryClient = useQueryClient()
  const { selectedSystem } = useAuthStore()

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateLocationTypeRequest }) => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.updateLocationType(selectedSystem, id, request)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LOCATION_TYPES_INVALIDATE_KEY })
    },
  })
}

export function useAdditionalTaxes() {
  const { selectedSystem } = useAuthStore()

  return useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.additionalTaxes(selectedSystem),
    queryFn: () => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.getAdditionalTaxes(selectedSystem)
    },
    enabled: !!selectedSystem,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: 'always',
  })
}

export function useAdditionalTax(machineName: string) {
  const { selectedSystem } = useAuthStore()

  return useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.additionalTax(selectedSystem, machineName),
    queryFn: () => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.getAdditionalTax(selectedSystem, machineName)
    },
    enabled: !!selectedSystem && machineName.length > 0,
  })
}

export function useCreateAdditionalTax() {
  const queryClient = useQueryClient()
  const { selectedSystem } = useAuthStore()

  return useMutation({
    mutationFn: (request: CreateAdditionalTaxRequest) => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.createAdditionalTax(selectedSystem, request)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADDITIONAL_TAXES_INVALIDATE_KEY })
    },
  })
}

export function useUpdateAdditionalTax() {
  const queryClient = useQueryClient()
  const { selectedSystem } = useAuthStore()

  return useMutation({
    mutationFn: ({
      machineName,
      request,
    }: {
      machineName: string
      request: UpdateAdditionalTaxRequest
    }) => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.updateAdditionalTax(selectedSystem, machineName, request)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADDITIONAL_TAXES_INVALIDATE_KEY })
    },
  })
}

export function useDBA(id: string) {
  const { selectedSystem } = useAuthStore()

  return useQuery({
    queryKey: LOCATIONS_QUERY_KEYS.dba(selectedSystem, id),
    queryFn: () => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.getDBA(selectedSystem, id)
    },
    enabled: !!selectedSystem && id.length > 0,
  })
}

export function useCreateDBA() {
  const queryClient = useQueryClient()
  const { selectedSystem } = useAuthStore()

  return useMutation({
    mutationFn: (request: CreateDBARequest) => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.createDBA(selectedSystem, request)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DBAS_INVALIDATE_KEY })
    },
  })
}

export function useUpdateDBA() {
  const queryClient = useQueryClient()
  const { selectedSystem } = useAuthStore()

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateDBARequest }) => {
      if (!selectedSystem) {
        throw new Error('No system selected')
      }
      return locationService.updateDBA(selectedSystem, id, request)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DBAS_INVALIDATE_KEY })
    },
  })
}
